import React, { useState } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../../contexts/NotificationContext';
import { api, buildImageUrl } from '../../../services/api';

interface SiteSettings {
  watermark_enabled: boolean;
  watermark_logo_path: string;
  watermark_position: string;
  watermark_opacity: number;
  watermark_size: number;
  watermark_margin: number;
  [key: string]: any;
}

interface WatermarkSettingsProps {
  settings: SiteSettings;
  onUpdate: (updates: Partial<SiteSettings>) => void;
}

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Switch = styled.div<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  input {
    display: none;
  }
  
  .switch {
    width: 50px;
    height: 24px;
    background: ${({ $enabled, theme }) => $enabled ? theme.colors.primary : '#ccc'};
    border-radius: 12px;
    position: relative;
    transition: background 0.3s;
    
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: ${({ $enabled }) => $enabled ? '26px' : '2px'};
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 50%;
      transition: left 0.3s;
    }
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RangeInput = styled.input`
  flex: 1;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const RangeValue = styled.span`
  min-width: 60px;
  text-align: center;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`;

const FileUploadArea = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  input {
    display: none;
  }
`;

const PreviewArea = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  align-items: center;
  
  img {
    max-width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
`;

const TestButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const WatermarkSettings: React.FC<WatermarkSettingsProps> = ({ settings, onUpdate }) => {
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [testImage, setTestImage] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const { showNotification } = useNotification();

  const handleToggleEnabled = () => {
    onUpdate({ watermark_enabled: !settings.watermark_enabled });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedLogo(file);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      setIsUploading(true);
      const response = await api.post('/watermark/upload-watermark-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onUpdate({ watermark_logo_path: response.data.logo_path });
      showNotification('Logo da marca d\'água enviado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      showNotification('Erro ao fazer upload do logo', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleTestWatermark = async () => {
    if (!testImage) {
      showNotification('Selecione uma imagem de teste', 'error');
      return;
    }

    if (!settings.watermark_logo_path && !selectedLogo) {
      showNotification('Configure um logo primeiro', 'error');
      return;
    }

    try {
      setIsTesting(true);
      showNotification('Preview gerado com sucesso!', 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Erro ao testar marca d\'água', 'error');
    } finally {
      setIsTesting(false);
    }
  };

  const fetchLogoAsBlob = async (path: string): Promise<Blob> => {
    try {
      const response = await fetch(`${api.defaults.baseURL}/media/${path}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.startsWith('image/')) {
        throw new Error('Resposta não é uma imagem válida');
      }
      
      return await response.blob();
    } catch (error) {
      throw new Error(`Erro ao buscar logo: ${error}`);
    }
  };

  return (
    <>
      <Section>
        <SectionTitle>🏷️ Configurações de Marca d'água</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Configure a marca d'água que será aplicada automaticamente nas imagens de propriedades.
        </p>

        <FormGroup>
          <Label>Ativar Marca d'água</Label>
          <Switch $enabled={settings.watermark_enabled} onClick={handleToggleEnabled}>
            <input 
              type="checkbox" 
              checked={settings.watermark_enabled} 
              onChange={handleToggleEnabled}
              readOnly
            />
            <div className="switch" />
            <span>{settings.watermark_enabled ? 'Ativada' : 'Desativada'}</span>
          </Switch>
          <HelpText>Quando ativada, todas as imagens de propriedades receberão automaticamente a marca d'água</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Logo da Marca d'água</Label>
          <FileUploadArea>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              id="logo-upload"
              disabled={isUploading}
            />
            <label htmlFor="logo-upload">
              <div>📎 {isUploading ? 'Enviando...' : 'Clique para selecionar o logo'}</div>
              <small>PNG, JPG - Recomendado: fundo transparente</small>
            </label>
          </FileUploadArea>
          {settings.watermark_logo_path && (
            <PreviewArea>
              <img src={buildImageUrl(settings.watermark_logo_path)} alt="Logo atual" />
              <span>Logo atual da marca d'água</span>
            </PreviewArea>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Posição da Marca d'água</Label>
          <Select 
            value={settings.watermark_position || 'center'} 
            onChange={(e) => onUpdate({ watermark_position: e.target.value })}
          >
            <option value="center">Centro</option>
            <option value="top-left">Superior Esquerda</option>
            <option value="top-right">Superior Direita</option>
            <option value="bottom-left">Inferior Esquerda</option>
            <option value="bottom-right">Inferior Direita</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Opacidade</Label>
          <RangeContainer>
            <RangeInput
              type="range"
              min="10"
              max="100"
              value={settings.watermark_opacity || 70}
              onChange={(e) => onUpdate({ watermark_opacity: Number(e.target.value) })}
            />
            <RangeValue>{settings.watermark_opacity || 70}%</RangeValue>
          </RangeContainer>
          <HelpText>Transparência da marca d'água (10% = muito transparente, 100% = opaco)</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Tamanho</Label>
          <RangeContainer>
            <RangeInput
              type="range"
              min="5"
              max="50"
              value={settings.watermark_size || 15}
              onChange={(e) => onUpdate({ watermark_size: Number(e.target.value) })}
            />
            <RangeValue>{settings.watermark_size || 15}%</RangeValue>
          </RangeContainer>
          <HelpText>Tamanho da marca d'água em relação à imagem original</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Margem</Label>
          <RangeContainer>
            <RangeInput
              type="range"
              min="5"
              max="100"
              value={settings.watermark_margin || 20}
              onChange={(e) => onUpdate({ watermark_margin: Number(e.target.value) })}
            />
            <RangeValue>{settings.watermark_margin || 20}px</RangeValue>
          </RangeContainer>
          <HelpText>Distância das bordas da imagem</HelpText>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>🧪 Teste de Marca d'água</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Teste como ficará a marca d'água antes de aplicar nas imagens reais.
        </p>

        <FormGroup>
          <Label>Imagem de Teste</Label>
          <FileUploadArea>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setTestImage(e.target.files?.[0] || null)}
              id="test-image"
            />
            <label htmlFor="test-image">
              <div>🖼️ Selecione uma imagem para testar</div>
              <small>Qualquer imagem para ver como ficará com a marca d'água</small>
            </label>
          </FileUploadArea>
          {testImage && (
            <PreviewArea>
              <img src={URL.createObjectURL(testImage)} alt="Imagem de teste" />
              <span>Imagem selecionada para teste</span>
            </PreviewArea>
          )}
        </FormGroup>
        
        <TestButton
          onClick={handleTestWatermark}
          disabled={isTesting || !testImage || (!settings.watermark_logo_path && !selectedLogo)}
        >
          {isTesting ? '⏳ Gerando Preview...' : '👁️ Gerar Preview'}
        </TestButton>
        
        <TestButton
          onClick={async () => {
            if (!testImage) {
              showNotification('Selecione uma imagem de teste', 'error');
              return;
            }

            if (!settings.watermark_logo_path && !selectedLogo) {
              showNotification('Configure um logo de marca d\'água primeiro', 'error');
              return;
            }

            const formData = new FormData();
            formData.append('image', testImage);
            
            if (selectedLogo) {
              formData.append('logo', selectedLogo);
            } else {
              // Tentar carregar logo configurado
              try {
                // O caminho já inclui /uploads/, então precisamos remover para buildImageUrl funcionar corretamente
                const cleanPath = settings.watermark_logo_path.startsWith('/uploads/') 
                  ? settings.watermark_logo_path.substring(9) // Remove '/uploads/'
                  : settings.watermark_logo_path;
                
                const possiblePaths = [
                  `http://localhost:8100${settings.watermark_logo_path}`, // Caminho direto com /uploads
                  `http://localhost:8100/uploads/${cleanPath}`, // Caminho sem duplicar /uploads
                  buildImageUrl(settings.watermark_logo_path),
                  buildImageUrl(cleanPath)
                ];
                
                let logoBlob = null;
                for (const path of possiblePaths) {
                  try {
                    const response = await fetch(path);
                    
                    if (response.ok) {
                      const contentType = response.headers.get('content-type');
                      if (contentType && contentType.startsWith('image/')) {
                        logoBlob = await response.blob();
                        break;
                      }
                    }
                  } catch (e) {
                    continue;
                  }
                }
                
                if (!logoBlob) {
                  throw new Error('Logo não encontrado em nenhum caminho tentado');
                }
                
                // Garantir que o tipo MIME está correto
                const logoType = logoBlob.type || 'image/png';
                const logoFile = new File([logoBlob], 'watermark-logo.png', { type: logoType });
                formData.append('logo', logoFile);
              } catch (error) {
                showNotification('Erro ao carregar logo. Use um logo novo.', 'error');
                return;
              }
            }
            
            formData.append('position', settings.watermark_position || 'center');
            formData.append('opacity', (settings.watermark_opacity || 70).toString());
            formData.append('size_percentage', (settings.watermark_size || 15).toString());
            formData.append('margin', (settings.watermark_margin || 20).toString());

            try {
              setIsTesting(true);
              
              // Usar axios diretamente SEM autenticação
              const response = await api.post('/watermark/test-watermark-debug', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                responseType: 'blob'
              });

              const previewUrl = URL.createObjectURL(response.data);
              window.open(previewUrl, '_blank');
              showNotification('Preview de debug gerado com sucesso!', 'success');
            } catch (error: any) {
              showNotification('Erro no endpoint de debug', 'error');
            } finally {
              setIsTesting(false);
            }
          }}
          disabled={isTesting || !testImage || (!settings.watermark_logo_path && !selectedLogo)}
          style={{ marginLeft: '1rem', background: '#fd7e14' }}
        >
          {isTesting ? '⏳ Gerando...' : '🔧 Teste Debug'}
        </TestButton>
      </Section>
    </>
  );
};

export default WatermarkSettings;
